import { useEffect, useState } from 'react';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';

export type MacOSOnboardingWindowStage = 'login' | 'onboarding';

type NativeWindowDragRegion = {
  height: number;
  width: number;
  x: number;
  y: number;
};

type NativeWindowDragRegions = {
  drag: NativeWindowDragRegion[];
  noDrag: NativeWindowDragRegion[];
};

export type MacOSOnboardingShortcutKeyInfo = {
  keys: string[];
};

type NativeOnboardingBridge = {
  callbackURLScheme?: () => Promise<string> | string;
  complete?: () => Promise<void> | void;
  getChatShortcutKeys?: () =>
    | Promise<MacOSOnboardingShortcutKeyInfo>
    | MacOSOnboardingShortcutKeyInfo;
  logout?: () => Promise<void> | void;
  openExternalURL?: (url: string) => Promise<void> | void;
  prepareStartupAnimation?: () => Promise<void> | void;
  setStage?: (stage: MacOSOnboardingWindowStage) => Promise<void> | void;
  setWindowDragRegions?: (
    regions: NativeWindowDragRegions
  ) => Promise<void> | void;
  syncSession?: () => Promise<void> | void;
};

type NativeJSB = {
  OnboardingBridge?: NativeOnboardingBridge;
};

const DRAG_SELECTOR = '[data-native-window-drag]';
const SESSION_SYNC_RETRY_DELAYS_MS = [0, 150, 400, 900];
const NO_DRAG_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
].join(',');
const FORCED_NO_DRAG_SELECTOR = [
  '[data-native-window-no-drag]',
  'button:disabled',
  'input:disabled',
  'select:disabled',
  'textarea:disabled',
  '[aria-disabled="true"]',
].join(',');
let activeWindowDragRegionSyncID = 0;

export function isMacOSOnboardingSurface(
  searchParams: URLSearchParams
): boolean {
  return searchParams.get('surface') === 'macos' && hasNativeJSBridge();
}

export function isMacOSOnboardingRuntime(searchParams: URLSearchParams) {
  if (!hasNativeJSBridge()) {
    return false;
  }
  if (searchParams.get('surface') === 'macos') {
    return true;
  }

  if (isOnboardingPath(window.location.pathname)) {
    return true;
  }
  if (window.location.pathname === '/login' && hasNativeOnboardingBridge()) {
    return true;
  }

  const next = searchParams.get('next');
  if (!next) {
    return false;
  }

  try {
    const nextURL = new URL(next, window.location.origin);
    return isOnboardingPath(nextURL.pathname);
  } catch {
    return false;
  }
}

function isOnboardingPath(pathname: string): boolean {
  return pathname === '/onboarding' || pathname.startsWith('/onboarding/');
}

export async function completeMacOSOnboarding(): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  const complete = bridge?.complete;

  if (typeof complete !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  await complete();
}

export async function logoutMacOSOnboarding(): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  const logout = bridge?.logout;

  if (typeof logout !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  await logout();
}

export async function getMacOSOnboardingChatShortcutKeys(): Promise<string[]> {
  const bridge = getNativeOnboardingBridge();
  const getChatShortcutKeys = bridge?.getChatShortcutKeys;

  if (typeof getChatShortcutKeys !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  const result = await getChatShortcutKeys();
  return Array.isArray(result.keys) ? result.keys : [];
}

export async function getMacOSOnboardingCallbackURLScheme(): Promise<string> {
  const bridge = getNativeOnboardingBridge();
  const callbackURLScheme = bridge?.callbackURLScheme;

  if (typeof callbackURLScheme !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  return callbackURLScheme();
}

export async function openMacOSOnboardingExternalURL(
  url: string
): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  const openExternalURL = bridge?.openExternalURL;

  if (typeof openExternalURL !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  await openExternalURL(url);
}

export async function syncMacOSOnboardingSession(): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  const syncSession = bridge?.syncSession;

  if (typeof syncSession !== 'function') {
    throw new Error('Native onboarding bridge is unavailable');
  }

  await syncMacOSOnboardingSessionAttempt(syncSession, 0);
}

async function syncMacOSOnboardingSessionAttempt(
  syncSession: NonNullable<NativeOnboardingBridge['syncSession']>,
  attemptIndex: number
): Promise<void> {
  const delayMs = SESSION_SYNC_RETRY_DELAYS_MS[attemptIndex];
  if (delayMs === undefined) {
    throw new Error('Failed to sync native onboarding session');
  }

  if (delayMs > 0) {
    await delay(delayMs);
  }

  try {
    await syncSession();
  } catch (error) {
    if (attemptIndex >= SESSION_SYNC_RETRY_DELAYS_MS.length - 1) {
      throw error;
    }
    await syncMacOSOnboardingSessionAttempt(syncSession, attemptIndex + 1);
  }
}

export function syncMacOSOnboardingSessionSoon(): void {
  void syncMacOSOnboardingSession().catch(error => {
    console.warn('Failed to sync native onboarding session', error);
  });
}

export function useMacOSOnboardingWindowRuntime({
  enabled,
  stage,
}: {
  enabled: boolean;
  stage: MacOSOnboardingWindowStage;
}) {
  const [stageReady, setStageReady] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setStageReady(true);
      return;
    }

    let disposed = false;
    setStageReady(false);
    void prepareMacOSOnboardingWindow(stage)
      .catch(reportNativeRuntimeError)
      .finally(() => {
        if (!disposed) {
          setStageReady(true);
        }
      });

    return () => {
      disposed = true;
    };
  }, [enabled, stage]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const syncID = ++activeWindowDragRegionSyncID;
    let frame = 0;
    let disposed = false;

    const syncNow = () => {
      if (disposed || activeWindowDragRegionSyncID !== syncID) {
        return;
      }
      void syncMacOSWindowDragRegions().catch(reportNativeRuntimeError);
    };

    const scheduleSync = () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        syncNow();
      });
    };

    syncNow();
    window.addEventListener('resize', scheduleSync);
    window.addEventListener('scroll', scheduleSync, true);

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(scheduleSync)
        : null;
    if (document.body) {
      mutationObserver?.observe(document.body, {
        attributes: true,
        attributeFilter: [
          'class',
          'style',
          'data-native-window-drag',
          'data-native-window-no-drag',
        ],
        childList: true,
        subtree: true,
      });
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleSync)
        : null;
    resizeObserver?.observe(document.documentElement);
    if (document.body) {
      resizeObserver?.observe(document.body);
    }

    return () => {
      disposed = true;
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('scroll', scheduleSync, true);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      window.requestAnimationFrame(() => {
        if (activeWindowDragRegionSyncID !== syncID) {
          return;
        }
        void setMacOSWindowDragRegions({ drag: [], noDrag: [] }).catch(
          reportNativeRuntimeError
        );
      });
    };
  }, [enabled]);

  return stageReady;
}

export function useMacOSOnboardingChatShortcutKeys(enabled: boolean): string[] {
  const [keys, setKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setKeys([]);
      return;
    }

    let disposed = false;
    const loadShortcutKeys = () => {
      void getMacOSOnboardingChatShortcutKeys()
        .then(nextKeys => {
          if (!disposed) {
            setKeys(nextKeys);
          }
        })
        .catch(reportNativeRuntimeError);
    };

    loadShortcutKeys();
    window.addEventListener('focus', loadShortcutKeys);

    return () => {
      disposed = true;
      window.removeEventListener('focus', loadShortcutKeys);
    };
  }, [enabled]);

  return keys;
}

async function setMacOSOnboardingStage(
  stage: MacOSOnboardingWindowStage
): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  if (typeof bridge?.setStage !== 'function') {
    return;
  }
  await bridge.setStage(stage);
}

async function prepareMacOSOnboardingWindow(
  stage: MacOSOnboardingWindowStage
): Promise<void> {
  await setMacOSOnboardingStage(stage);

  if (stage !== 'onboarding') {
    return;
  }

  const bridge = getNativeOnboardingBridge();
  if (typeof bridge?.prepareStartupAnimation !== 'function') {
    return;
  }

  await bridge.prepareStartupAnimation();
}

async function syncMacOSWindowDragRegions(): Promise<void> {
  await setMacOSWindowDragRegions({
    drag: collectElementRects(DRAG_SELECTOR, {
      allowPointerEventsNone: true,
    }),
    noDrag: dedupeWindowDragRegions([
      ...collectElementRects(NO_DRAG_SELECTOR),
      ...collectElementRects(FORCED_NO_DRAG_SELECTOR, {
        allowPointerEventsNone: true,
      }),
    ]),
  });
}

async function setMacOSWindowDragRegions(
  regions: NativeWindowDragRegions
): Promise<void> {
  const bridge = getNativeOnboardingBridge();
  if (typeof bridge?.setWindowDragRegions !== 'function') {
    return;
  }
  await bridge.setWindowDragRegions(regions);
}

function getNativeOnboardingBridge(): NativeOnboardingBridge | undefined {
  const jsb = (window as Window & { jsb?: unknown }).jsb as
    | NativeJSB
    | undefined;
  return jsb?.OnboardingBridge;
}

function hasNativeOnboardingBridge(): boolean {
  return getNativeOnboardingBridge() !== undefined;
}

function delay(durationMs: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, durationMs));
}

function dedupeWindowDragRegions(
  regions: NativeWindowDragRegion[]
): NativeWindowDragRegion[] {
  const seen = new Set<string>();
  return regions.filter(region => {
    const key = `${region.x}:${region.y}:${region.width}:${region.height}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function collectElementRects(
  selector: string,
  options: {
    allowPointerEventsNone?: boolean;
  } = {}
): NativeWindowDragRegion[] {
  if (typeof document === 'undefined') {
    return [];
  }

  return Array.from(document.querySelectorAll(selector)).flatMap(element => {
    if (!(element instanceof HTMLElement)) {
      return [];
    }

    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      (!options.allowPointerEventsNone && style.pointerEvents === 'none')
    ) {
      return [];
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return [];
    }

    return [
      {
        height: Math.round(rect.height),
        width: Math.round(rect.width),
        x: Math.round(rect.left),
        y: Math.round(rect.top),
      },
    ];
  });
}

function reportNativeRuntimeError(error: unknown) {
  console.warn('Failed to update native onboarding window', error);
}
